// Shared identifiers for the React Flow node `type` discriminator and the
// handle IDs used on each node kind. Centralised so editor, canvas, layout,
// and execution view all reference the same string literals.

export const TRIGGER = "trigger" as const
export const CONDITION = "condition" as const
export const ACTION = "action" as const
export const BRANCH = "branch" as const

export type NodeKind =
  | typeof TRIGGER
  | typeof CONDITION
  | typeof ACTION
  | typeof BRANCH

/** Uppercase wire-type discriminators used by both the API node payload
 *  (`WorkflowApiNode.type`) and the canvas data shape (`WorkflowNodeData
 *  .nodeType`). Distinct from the lowercase React Flow `type` constants
 *  above — the lowercase ones drive `nodeTypes` registration, these drive
 *  the runtime type checks and the serialised wire format. */
export const NODE_TYPE_TRIGGER = "TRIGGER" as const
export const NODE_TYPE_CONDITION = "CONDITION" as const
export const NODE_TYPE_ACTION = "ACTION" as const
export const NODE_TYPE_BRANCH = "BRANCH" as const

export type NodeTypeDiscriminator =
  | typeof NODE_TYPE_TRIGGER
  | typeof NODE_TYPE_CONDITION
  | typeof NODE_TYPE_ACTION
  | typeof NODE_TYPE_BRANCH

/** Default source/target handle id used by trigger and action nodes, and
 *  the target handle id used by every node. */
export const HANDLE_OUTPUT = "output" as const
export const HANDLE_INPUT = "input" as const

/** Source handle ids on a condition node — one per branch. */
export const HANDLE_THEN = "true-output" as const
export const HANDLE_OTHERWISE = "false-output" as const

export type ConditionBranchHandle = typeof HANDLE_THEN | typeof HANDLE_OTHERWISE

/** Source handle id on a BRANCH node for the implicit fallback path. */
export const HANDLE_BRANCH_OTHERWISE = "branch-otherwise" as const

/** Hard cap on parallel children attached to any single source handle on
 *  any node (trigger output, condition then / otherwise, branch case /
 *  otherwise, action output). Mirrors the backend's `then[]` / `otherwise[]`
 *  array length limit and applies equally to BRANCH cases. */
export const MAX_PARALLEL_CHILDREN = 10

/** Build the source handle id for case `caseId` on a BRANCH node. Cardinality
 *  is dynamic so handle ids are keyed by the case's stable client id rather
 *  than its index — reordering must not break already-attached edges. */
export const branchCaseHandle = (caseId: string): string => `branch-case-${caseId}`

/** True when `handle` is a BRANCH case handle (i.e. `branch-case-…`). */
export const isBranchCaseHandle = (handle: string | null | undefined): boolean =>
  typeof handle === "string" && handle.startsWith("branch-case-")
